import { useState, useEffect, useCallback, useRef } from 'react';
import { academyStore } from '../services/academyStore';
import { AcademySubscriptionPlan, AcademySubscriptionInvoice, SubscriptionUsage } from '../types/academy';

export function useSubscription() {
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [plans, setPlans] = useState<AcademySubscriptionPlan[]>([]);
  const [invoices, setInvoices] = useState<AcademySubscriptionInvoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const isFetchingRef = useRef<boolean>(false);

  const loadData = useCallback(async (force = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const [u, p, inv] = await Promise.all([
        academyStore.getSubscriptionUsage(force),
        academyStore.getSubscriptionPlans(force),
        academyStore.getSubscriptionInvoices(force),
      ]);
      setUsage(u);
      setPlans(p);
      setInvoices(inv);
    } catch (err) {
      console.error('Failed to load subscription data:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadData(false);

    const unsub = academyStore.subscribe(() => {
      loadData(false);
    });

    return () => unsub();
  }, [loadData]);

  // Feature checks
  const isFeatureAllowed = useCallback((featureCode: string): boolean => {
    if (!usage) return true;
    if (usage.planCode === 'ENTERPRISE') return true;
    try {
      const features: string[] = JSON.parse(usage.featuresJson || '[]');
      return features.includes(featureCode);
    } catch {
      return false;
    }
  }, [usage]);

  // Quota indicators
  const isStudentLimitReached = usage
    ? usage.studentsMax > 0 && usage.studentsUsed >= usage.studentsMax
    : false;

  const isTeacherLimitReached = usage
    ? usage.teachersMax > 0 && usage.teachersUsed >= usage.teachersMax
    : false;

  const isTrial = usage?.status === 'trial';
  const isExpired = usage?.status === 'expired';

  // Trial countdown
  const remainingTrialDays = (() => {
    if (!usage?.trialEndDate) return 0;
    const end = new Date(usage.trialEndDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  })();

  return {
    usage,
    plans,
    invoices,
    loading,
    refreshUsage: () => {
      academyStore.invalidateSubscription();
      loadData(true);
    },
    isFeatureAllowed,
    isStudentLimitReached,
    isTeacherLimitReached,
    isTrial,
    isExpired,
    remainingTrialDays,
  };
}
